// @vitest-environment jsdom
import { TempoFilterConfig } from 'constants/filters';
import React, { useReducer, useCallback, useRef } from 'react';
import {
    Button,
    FormControl,
    CloseOutlined,
    CaretLeftOutlined,
    Box,
    Flex,
    Input,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import { useMountEffect } from 'hooks/useMountEffect';
import Draggable from './Draggable';
import { initReducer, bpmSliderReducer } from './BpmControl.reducer';

/**
 * COMPONENT
 */

export interface BpmControlProps {
    onApply: (newBpm: number) => void;
    onClick: () => void;
    initialBpm: number;
}

export const BpmControl: React.FC<BpmControlProps> = ({
    onApply,
    onClick,
    initialBpm,
}: BpmControlProps): JSX.Element => {
    // REDUCER
    const [{ imperativePosition, selectedBpm, maxSliderPosition }, dispatch] =
        useReducer(bpmSliderReducer, initialBpm, initReducer);

    // REFS
    const containerRef = useRef<HTMLDivElement>(null);
    const railRef = useRef<HTMLDivElement>(null);

    // EFFECTS
    // Adjust the slider scaling when the container changes size
    useMountEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            // HACK: The Popover takes 200ms to finish loading, and because it expands
            // it means the width of the contents are unstable until it's finished
            // its transition. Popover does not expose any callbacks to handle
            // transition finished events.
            setTimeout(() => {
                if (railRef.current) {
                    let { width } = railRef.current.getBoundingClientRect();

                    width -= TempoFilterConfig.ButtonRadius / 2;

                    if (width !== maxSliderPosition) {
                        dispatch({
                            type: 'UPDATE_SCALE',
                            newMaxSliderPosition: width,
                        });
                    }
                }
            }, 200);
        });

        railRef.current && resizeObserver.observe(railRef.current);

        return (): void => {
            railRef.current && resizeObserver.unobserve(railRef.current);
        };
    });

    // CALLBACKS
    const handleFocus = useCallback((): void => {
        containerRef?.current?.focus();
    }, []);

    const handleInputBlur = useCallback(
        (
            event:
                | React.FocusEvent<HTMLInputElement>
                | React.KeyboardEvent<Element>,
        ) => {
            const { value } = event.target as HTMLInputElement;

            let intValue = parseInt(value, 10);

            if (isNaN(intValue)) {
                return;
            }

            if (intValue < TempoFilterConfig.MinBpm) {
                intValue = TempoFilterConfig.MinBpm;
            }
            if (intValue > TempoFilterConfig.MaxBpm) {
                intValue = TempoFilterConfig.MaxBpm;
            }

            dispatch({ type: 'UPDATE_BPM', value: intValue });
        },
        [dispatch],
    );

    const handleBpmInputChange = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const { value } = event.target as HTMLInputElement;

            dispatch({ type: 'UPDATE_BPM', value: Number(value) });
        },
        [dispatch],
    );

    const handleSliderChange = useCallback(
        (position: number) => {
            dispatch({ type: 'UPDATE_POSITION', position });
        },
        [dispatch],
    );

    const handleClear = useCallback((): void => {
        dispatch({ type: 'RESET' });
    }, [dispatch]);

    const handleApply = useCallback((): void => {
        onApply(selectedBpm);
        onClick();
    }, [onApply, onClick, selectedBpm]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleApply();
            }
        },
        [handleApply],
    );

    const handleBpmKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.stopPropagation();
                handleInputBlur(event);
                handleFocus();
            }
        },
        [handleInputBlur, handleFocus],
    );

    return (
        <Flex
            width={{ base: 'auto', md: '480px' }}
            height={{ base: 'auto', md: '220px' }}
            backgroundColor="surface.low"
            flexDirection="column"
            justifyContent="space-between"
            outline="none"
            onKeyDown={handleKeyDown}
            tabIndex={0}
            ref={containerRef}
        >
            <Flex alignItems="center" justifyContent="space-between">
                <Button
                    color="text.medium"
                    float="left"
                    fontSize="lg"
                    marginTop="lg"
                    display={{ base: 'inherit', md: 'none' }}
                    kind="minimal"
                    icon={CaretLeftOutlined}
                    onClick={onClick}
                >
                    <Trans>Back</Trans>
                </Button>
            </Flex>
            <Flex
                height="100%"
                flexDirection="column"
                justifyContent="space-between"
                padding="lg"
            >
                <FormControl>
                    <FormControl.Label htmlFor="creator-bpm-input" isHidden>
                        <Trans>Enter BPM</Trans>
                    </FormControl.Label>
                    <Input
                        maxWidth="72px"
                        value={selectedBpm.toString()}
                        type="number"
                        onBlur={handleInputBlur}
                        onChange={handleBpmInputChange}
                        onKeyDown={handleBpmKeyDown}
                        size="md"
                        data-testid="creator-bpm-input"
                        id="creator-bpm-input"
                    />
                </FormControl>
                <Box
                    width="100%"
                    paddingY="lg"
                    paddingX="xl"
                    float="left"
                    position="relative"
                    userSelect="none"
                    onClick={handleFocus}
                >
                    <Box
                        position="absolute"
                        width="calc(100% - 28px)"
                        left="14px"
                    >
                        <Box
                            width="100%"
                            height="1px"
                            backgroundColor="neutral.600"
                            ref={railRef}
                        />
                        <Draggable
                            data-testid="draggable-testid"
                            horizontalPosition={0}
                            draggableRadius={
                                TempoFilterConfig.DraggableZoneRadius
                            }
                            styledButtonRadius={TempoFilterConfig.ButtonRadius}
                            onChange={handleSliderChange}
                            minSliderPosX={0}
                            maxSliderPosX={maxSliderPosition}
                            imperativePosition={imperativePosition}
                            maxRange={maxSliderPosition}
                            isSingleValue
                        />
                    </Box>
                </Box>
            </Flex>
            <Flex
                minHeight={{ base: 'auto', md: 'lg' }}
                justifyContent="space-between"
                alignItems="center"
                paddingY="lg"
                paddingX={{ base: 'none', md: 'lg' }}
                borderTop="base"
            >
                <Button
                    variant="secondary"
                    isRounded
                    kind="minimal"
                    icon={CloseOutlined}
                    onClick={handleClear}
                    aria-label={t`Clear`}
                >
                    <Trans>Clear</Trans>
                </Button>
                <Button
                    isRounded
                    variant="primary"
                    kind="minimal"
                    onClick={handleApply}
                    aria-label={t`Apply`}
                >
                    <Trans>Apply</Trans>
                </Button>
            </Flex>
        </Flex>
    );
};
